import React from "react";
import { DateOrDateString } from "../../../defines";
import { dateToYYYYMMDD, fixDateToUTCTime } from "../../../utils/dates";
import {
  getDateValue,
  isDateAllowed,
  selectedDateConsideringNegativeTimezoneOffset,
} from "../utils";
import {
  VeriDatePickerContext,
  VeriDatePickerContextProps,
  VeriDatePickerState,
  VeriDatePickerView,
} from "./VeriDatePickerContext.types";

export const DatePickerContext = React.createContext<VeriDatePickerContext>(
  {} as VeriDatePickerContext,
);

const VeriDatePickerProvider: React.FC<VeriDatePickerContextProps> = ({
  initialDate = null,
  initialDateString,
  onDayPick,
  onDateChange,
  onRangeChange,
  maxDate,
  minDate,
  children,
  enabledDates,
  disabledDates,
  isRangePicker,
}) => {
  const _initialDate = new Date();
  let _initialDateString = getDateValue(
    `${
      _initialDate.getMonth() + 1
    }/${_initialDate.getDate()}/${_initialDate.getFullYear()}`,
  );

  if (initialDateString && !isNaN(Date.parse(initialDateString))) {
    _initialDateString = getDateValue(initialDateString);
  }

  const [datePickerState, setDatePickerState] =
    React.useState<VeriDatePickerState>({
      date: new Date(initialDate || new Date()),
      nowDate: new Date(),
      selectedDate: new Date(initialDate || new Date()),
      dateString: _initialDateString,
      selectedDateString: "",
      rangeStartDate: "",
      rangeEndDate: "",
      view: VeriDatePickerView.Days,
      yearStep: 0,
      onDayPick: onDayPick || (() => null),
      maxDate,
      minDate,
      enabledDates,
      disabledDates,
      isRangePicker,
      onDateChange,
      onRangeChange,
    });

  const isDateEnabled = React.useCallback(
    (date: DateOrDateString) => {
      return isDateAllowed(date, {
        enabledDates: datePickerState.enabledDates,
        disabledDates: datePickerState.disabledDates,
        minDate: datePickerState.minDate,
        maxDate: datePickerState.maxDate,
      });
    },
    [
      datePickerState.maxDate,
      datePickerState.minDate,
      datePickerState.enabledDates,
      datePickerState.disabledDates,
    ],
  );

  const changeState: VeriDatePickerContext["changeState"] = React.useCallback(
    (key, value) => {
      setDatePickerState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const isDateInRange: VeriDatePickerContext["isDateInRange"] = (date) => {
    if (!datePickerState.isRangePicker) return false;

    const rangeStartDateSelected = datePickerState.rangeStartDate
      ? selectedDateConsideringNegativeTimezoneOffset(
          datePickerState.rangeStartDate,
        )
      : undefined;

    const rangeEndDateSelected = datePickerState.rangeEndDate
      ? selectedDateConsideringNegativeTimezoneOffset(
          datePickerState.rangeEndDate,
        )
      : undefined;

    const _date = fixDateToUTCTime(date);
    const _startRange = rangeStartDateSelected
      ? fixDateToUTCTime(rangeStartDateSelected)
      : null;
    const _endRange = rangeEndDateSelected
      ? fixDateToUTCTime(rangeEndDateSelected)
      : null;

    if (_date && _startRange && _endRange) {
      return _date >= _startRange && _date <= _endRange;
    }

    if (_date && _startRange) {
      return String(_date) == String(_startRange);
    }

    return false;
  };

  const isFirstDateInRange: VeriDatePickerContext["isFirstDateInRange"] = (
    date,
  ) => {
    if (isDateInRange(date)) {
      const rangeStartDateSelected = datePickerState.rangeStartDate
        ? selectedDateConsideringNegativeTimezoneOffset(
            datePickerState.rangeStartDate,
          )
        : undefined;
      const _date = fixDateToUTCTime(date);
      const _startRange = rangeStartDateSelected
        ? fixDateToUTCTime(rangeStartDateSelected)
        : null;
      return String(_date) == String(_startRange);
    }
    return false;
  };

  const isLastDateInRange: VeriDatePickerContext["isLastDateInRange"] = (
    date,
  ) => {
    if (isDateInRange(date)) {
      const rangeEndDateSelected = datePickerState.rangeEndDate
        ? selectedDateConsideringNegativeTimezoneOffset(
            datePickerState.rangeEndDate,
          )
        : undefined;
      const _date = fixDateToUTCTime(date);
      const _endRange = rangeEndDateSelected
        ? fixDateToUTCTime(rangeEndDateSelected)
        : null;
      return String(_date) == String(_endRange);
    }
    return false;
  };

  const setRangeDates: VeriDatePickerContext["setRangeDates"] = (
    dateStart: DateOrDateString,
    dateEnd: DateOrDateString,
  ) => {
    if (datePickerState.isRangePicker) {
      const dateStartStr =
        typeof dateStart === "string" ? dateStart : dateToYYYYMMDD(dateStart);
      const dateEndStr =
        typeof dateEnd === "string" ? dateEnd : dateToYYYYMMDD(dateEnd);

      let newRangeStartDate = dateStartStr;
      let newRangeEndDate = dateEndStr;

      let isStartAfterEnd = false;

      if (dateStartStr && dateEndStr) {
        isStartAfterEnd =
          fixDateToUTCTime(dateStartStr) > fixDateToUTCTime(dateEndStr);

        newRangeStartDate = isStartAfterEnd ? dateEndStr : dateStartStr;
        newRangeEndDate = isStartAfterEnd ? dateStartStr : dateEndStr;
      }

      datePickerState?.onRangeChange?.(
        newRangeStartDate ? fixDateToUTCTime(newRangeStartDate) : null,
        newRangeEndDate ? fixDateToUTCTime(newRangeEndDate) : null,
      );

      setDatePickerState((prev) => ({
        ...prev,
        rangeStartDate: newRangeStartDate,
        rangeEndDate: newRangeEndDate,
      }));
    }
  };

  const setDateValue: VeriDatePickerContext["setDateValue"] = (date) => {
    const dateStr = typeof date === "string" ? date : dateToYYYYMMDD(date);

    changeState("selectedDateString", dateStr);
    changeState("dateString", dateStr);
  };
  const setDate: VeriDatePickerContext["setDate"] = (
    date: DateOrDateString,
  ) => {
    const dateStr =
      typeof date === "string"
        ? date
        : dateToYYYYMMDD(
            new Date(date.getTime() - date.getTimezoneOffset() * 60000),
          );

    if (datePickerState.isRangePicker) {
      let newRangeStartDate = datePickerState.rangeStartDate;
      let newRangeEndDate = datePickerState.rangeEndDate;

      if (datePickerState.rangeStartDate) {
        if (datePickerState.rangeEndDate) {
          changeState("rangeStartDate", dateStr);
          changeState("rangeEndDate", "");
        } else {
          if (
            fixDateToUTCTime(datePickerState.rangeStartDate) >
            fixDateToUTCTime(dateStr)
          ) {
            newRangeStartDate = dateStr;
            newRangeEndDate = datePickerState.rangeStartDate;

            changeState("rangeStartDate", newRangeStartDate);
            changeState("rangeEndDate", newRangeEndDate);
          } else {
            newRangeEndDate = dateStr;
            changeState("rangeEndDate", newRangeEndDate);
          }
        }
      } else {
        newRangeStartDate = dateStr;
        changeState("rangeStartDate", dateStr);
      }

      datePickerState.onRangeChange?.(
        newRangeStartDate ? fixDateToUTCTime(newRangeStartDate) : null,
        newRangeEndDate ? fixDateToUTCTime(newRangeEndDate) : null,
      );

      return;
    }

    setDateValue(date);
    datePickerState.onDateChange?.(dateStr);
  };

  return (
    <DatePickerContext.Provider
      value={{
        ...datePickerState,
        setDatePickerState,
        changeState,
        isDateEnabled,
        setDate,
        setDateValue,
        isDateInRange,
        isFirstDateInRange,
        isLastDateInRange,
        setRangeDates,
      }}
    >
      {children}
    </DatePickerContext.Provider>
  );
};

const useVeriDatePicker = () => {
  const context = React.useContext(DatePickerContext);
  if (context === undefined) {
    throw new Error(
      "useVeriDatePicker must be used within a VeriDatePickerProvider",
    );
  }
  return context;
};

export { VeriDatePickerProvider, useVeriDatePicker };
